// The GENERAL AREA an event happened in, for the chip that leads a feed title:
// the country, or the named water. The feed titles an event by its most
// specific place ("Hayfan"), which is the right claim and useless to anyone
// who has never heard of Hayfan — "Yemen" in front of it is what makes a column
// of rows readable at a glance.
//
// Land reads the country the extractor stated (`attrs.country`, the one the
// geocoder already checks the pin against), else the admin-0 area the store
// stamped. Water can't: a tanker hit in the Strait of Hormuz carries "Iran" or
// "Oman" as its country, and "Iran" is the wrong glance for it. So a MARITIME
// event — or one whose place names the water, like an interception over the
// strait — is named by where its pin sits among a handful of coarse outlines,
// checked in order so the straits win over the gulfs around them. The outlines
// run generously over land on purpose: they are only asked about a point the
// extractor already put in the water, so a coastline must be wide, not exact.

import type { EntityDto } from './api';

type Pt = [number, number];

export interface Area {
  /** What the chip says: "Yemen", "Strait of Hormuz", "off Oman". */
  label: string;
  water: boolean;
  /** The tooltip — the finer reading behind the label. */
  title: string;
}

const box = (w: number, s: number, e: number, n: number): Pt[] =>
  [[w, s], [e, s], [e, n], [w, n]];

// Order is precedence: the first outline holding the point names it.
export const WATERS: ReadonlyArray<{ name: string; ring: Pt[] }> = [
  { name: 'Bab el-Mandeb', ring: box(42.8, 12.2, 43.8, 13.3) },
  { name: 'Strait of Hormuz', ring: box(55.8, 25.9, 56.9, 27.2) },
  { name: 'Persian Gulf', ring: box(47.5, 23.5, 56.0, 30.6) },
  { name: 'Gulf of Oman', ring: box(56.0, 22.3, 62.0, 26.6) },
  // Down the African shore, round the southern tip, up the Arabian one.
  { name: 'Red Sea', ring: [
    [32.2, 30.1], [32.4, 29.0], [33.3, 27.0], [35.5, 23.0], [37.0, 19.5],
    [39.0, 15.8], [41.5, 13.3], [42.7, 12.4], [43.5, 12.6], [43.6, 13.4],
    [43.3, 15.5], [42.2, 18.0], [40.2, 21.0], [38.8, 24.2], [35.3, 28.0],
    [35.3, 29.6]] },
  // Along the Yemeni shore — which climbs north going east, so Mocha stays
  // Red Sea — then back along Somalia and into the Gulf of Tadjoura.
  { name: 'Gulf of Aden', ring: [
    [43.4, 12.9], [44.5, 13.0], [46.0, 13.8], [49.0, 14.9], [52.3, 15.8],
    [52.3, 11.0], [51.0, 10.5], [47.0, 9.8], [44.0, 9.8], [42.4, 11.4],
    [42.4, 12.0]] },
  { name: 'Arabian Sea', ring: box(52.0, 5.0, 75.0, 22.5) },
  { name: 'E. Mediterranean', ring: box(28.0, 30.8, 36.5, 37.5) },
  { name: 'Caspian Sea', ring: box(46.5, 36.5, 55.2, 47.5) },
  { name: 'Sea of Azov', ring: box(34.8, 45.2, 39.4, 47.3) },
  { name: 'Black Sea', ring: box(27.3, 40.9, 41.8, 46.8) },
];

const WATER_WORDS = /\b(straits?|gulf|sea|bab[\s-]?[ae]l[\s-]?mand[ae]b|ocean)\b/i;

// Chip-length names; the tooltip keeps the full one.
const SHORT: Record<string, string> = {
  'united arab emirates': 'UAE', 'united states': 'US',
  'united states of america': 'US', 'united kingdom': 'UK',
};
const MAX_LABEL = 24;

const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
const clip = (s: string): string => (s.length > MAX_LABEL ? `${s.slice(0, MAX_LABEL - 1)}…` : s);
const short = (country: string): string => SHORT[country.toLowerCase()] ?? country;
const norm = (s: string): string => s.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();

function inRing([x, y]: Pt, ring: Pt[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]!;
    const [xj, yj] = ring[j]!;
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

export function waterAt(p: Pt): string | null {
  return WATERS.find((w) => inRing(p, w.ring))?.name ?? null;
}

function pointOf(e: EntityDto): Pt | null {
  const g = e.geometry as { type?: string; coordinates?: unknown } | null;
  if (g?.type !== 'Point' || !Array.isArray(g.coordinates)) return null;
  const [lon, lat] = g.coordinates as number[];
  return Number.isFinite(lon) && Number.isFinite(lat) ? [lon!, lat!] : null;
}

export function areaFor(e: EntityDto): Area | null {
  const a = e.attrs;
  const country = str(a.country) || str(a.admin0);
  const place = str(a.place);
  const maritime = a.setting === 'maritime';
  if (maritime || WATER_WORDS.test(place)) {
    const p = pointOf(e);
    const water = p ? waterAt(p) : null;
    const phrase = str(a.locationText) || place;
    if (water) {
      return { label: water, water: true,
        title: phrase && norm(phrase) !== norm(water) ? `${water} — ${phrase}` : water };
    }
    if (maritime) {
      const label = country ? `off ${short(country)}` : 'at sea';
      return { label: clip(label), water: true, title: phrase || label };
    }
  }
  if (!country) return null;
  const finer = str(a.region) || str(a.admin1);
  return { label: clip(short(country)), water: false,
    title: finer && norm(finer) !== norm(country) ? `${finer}, ${country}` : country };
}

/** A group's area is its members' most common one — the first seen on a tie,
 *  and members arrive newest first. */
export function groupArea(events: readonly EntityDto[]): Area | null {
  const tally = new Map<string, { area: Area; n: number }>();
  for (const e of events) {
    const area = areaFor(e);
    if (!area) continue;
    const seen = tally.get(norm(area.label));
    if (seen) seen.n += 1;
    else tally.set(norm(area.label), { area, n: 1 });
  }
  let best: { area: Area; n: number } | null = null;
  for (const t of tally.values()) if (!best || t.n > best.n) best = t;
  return best?.area ?? null;
}

/** The chip adds nothing when the title's place already IS the area
 *  ("Jordan", "Strait of Hormuz — Explosion"). */
export function redundantArea(area: Area, shown: string): boolean {
  return norm(shown.split(' — ')[0] ?? shown) === norm(area.label);
}
